import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export const MisCentrosCostoPage = () => {
  const { user } = useAuth();
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedCentro, setSelectedCentro] = useState(null);
  const [newCentro, setNewCentro] = useState('');

  useEffect(() => {
    if (user?.cliente_id) {
      loadCentrosCosto();
    }
  }, [user]);

  const loadCentrosCosto = async () => {
    try {
      const response = await api.get(`/clientes/${user.cliente_id}/centros-costo`);
      setCentrosCosto(response.data.centros_costo || []);
    } catch (error) {
      toast.error('Error al cargar centros de costo');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newCentro.trim()) {
      toast.error('Ingrese un nombre para el centro de costo');
      return;
    }

    if (centrosCosto.includes(newCentro.trim())) {
      toast.error('Este centro de costo ya existe');
      return;
    }

    try {
      const nuevosCentros = [...centrosCosto, newCentro.trim()];
      await api.put(`/clientes/${user.cliente_id}/centros-costo`, {
        centros_costo: nuevosCentros
      });
      setCentrosCosto(nuevosCentros);
      setNewCentro('');
      setShowAdd(false);
      toast.success('Centro de costo agregado');
    } catch (error) {
      toast.error('Error al agregar centro de costo');
    }
  };

  const handleDelete = async () => {
    try {
      const nuevosCentros = centrosCosto.filter(c => c !== selectedCentro);
      await api.put(`/clientes/${user.cliente_id}/centros-costo`, {
        centros_costo: nuevosCentros
      });
      setCentrosCosto(nuevosCentros);
      setShowDelete(false);
      setSelectedCentro(null);
      toast.success('Centro de costo eliminado');
    } catch (error) {
      toast.error('Error al eliminar centro de costo');
    }
  };

  if (!user?.cliente_id) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No tiene un cliente asociado para gestionar centros de costo.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mis-centros-costo-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Centros de Costo</h1>
          <p className="text-gray-600 mt-1">Gestione los centros de costo de {user?.cliente_nombre}</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="gap-2" data-testid="add-centro-btn">
          <Plus size={18} />
          Agregar Centro de Costo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 size={20} />
            Mis Centros de Costo
          </CardTitle>
          <CardDescription>
            Los centros de costo se utilizan para clasificar los viajes solicitados.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando...</div>
          ) : centrosCosto.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay centros de costo registrados. Agregue uno para clasificar sus viajes.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Nombre del Centro de Costo</TableHead>
                  <TableHead className="w-24 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {centrosCosto.map((centro, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell>{centro}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedCentro(centro);
                          setShowDelete(true);
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-centro-${index}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog para agregar */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Centro de Costo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="centro">Nombre del Centro de Costo *</Label>
              <Input
                id="centro"
                value={newCentro}
                onChange={(e) => setNewCentro(e.target.value)}
                placeholder="Ej: Operaciones, Administración, Proyectos"
                data-testid="new-centro-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} data-testid="save-centro-btn">
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog para eliminar */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar centro de costo?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro que desea eliminar el centro de costo "{selectedCentro}"? 
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
